import classNames from "classnames"
import { uniqWith } from "lodash"
import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"

import { useTranslation } from "../../i18n/translate"
import ArrowDownIcon from "../../images/icons/arrow_down.svg"
import { ProductModel } from "../../model/productModel"
import { observeDOM } from "../../util/observe-dom"
import Effect from "../effect/effect"
import Tile from "../tile/tile"
import "./effects-legend.sass"

const EffectsLegendHoc = (props) => {
  const { t } = useTranslation()
  const containerRef = useRef()

  const [left, setLeft] = useState(0)
  const [width, setWidth] = useState(0)
  const [show, setShow] = useState(!!props.show)

  const isInViewport = () => {
    if (!containerRef) return false
    const top = containerRef.current.getBoundingClientRect().top
    const bottom = containerRef.current.getBoundingClientRect().bottom
    return top < window.innerHeight && bottom > 0
  }

  useEffect(() => {
    function handleResize() {
      setLeft(containerRef.current?.getBoundingClientRect().left)
      setWidth(containerRef.current?.clientWidth)
    }

    function handleScroll() {
      setShow(isInViewport())
    }

    setLeft(containerRef.current?.getBoundingClientRect().left)
    setWidth(containerRef.current?.clientWidth)
    setShow(isInViewport())

    window.addEventListener("resize", handleResize)
    window.addEventListener("scroll", handleScroll)

    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  return (
    <>
      <div ref={containerRef}>{props.children}</div>

      <EffectsLegend
        t={t}
        show={show}
        left={left}
        width={width}
        products={props.products}
        productParamGroups={props.productParamGroups}
      />
    </>
  )
}

EffectsLegendHoc.propTypes = {
  children: PropTypes.node,
  products: PropTypes.arrayOf(PropTypes.instanceOf(ProductModel)),
}

class EffectsLegend extends React.Component {
  constructor(props) {
    super(props)

    this.contentRef = React.createRef()

    this.state = {
      contentHeight: 0,
      collapsed: true,
      effects: this._getEffectsForProducts(this.props.products, this.props.productParamGroups),
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.width !== prevProps.width) {
      this._updateHeight()
    }
    if (this.props.products !== prevProps.products) {
      this.setState({
        effects: this._getEffectsForProducts(this.props.products, this.props.productParamGroups),
      })
    }
  }

  componentDidMount() {
    this._updateHeight()
    this.domObserver = observeDOM()(this.contentRef.current, () =>
      this._updateHeight(),
    )
  }

  componentWillUnmount() {
    this.domObserver.disconnect()
  }

  _getEffectsForProducts(products = [], productParamGroups = undefined) {
    const { t } = this.props
    const effects = []

    products.forEach((product) => {
      return product.effect_product_parameter_values.forEach((effect) => {

        // if productParamGroups is passed only display effects of this groups
        if (productParamGroups) {
          const index = productParamGroups.findIndex(group => group.strapiId === effect.product_parameter_group.id);
          if (index === -1) {
            return;
          }
        }
        return effects.push(effect)
      })
    })

    return uniqWith(effects, (a, b) => {
      return (
        t(a.label.key) === t(b.label.key) &&
        a.color === b.color &&
        a.value === b.value
      )
    }).sort((a, b) => {
      return t(a.label.key).localeCompare(t(b.label.key))
    })
  }

  _updateHeight() {
    const height = this.contentRef.current.clientHeight
    if (this.state.height !== height) {
      this.setState({ height })
    }
  }

  render() {
    const { t } = this.props
    const { effects, collapsed, height } = this.state

    return (
      <div
        style={{ width: this.props.width, left: this.props.left }}
        className={classNames(
          "_fp-effects-legend",
          { "_fp-effects-legend--collapsed": collapsed },
          {
            "_fp-effects-legend--hidden":
              !this.props.show || effects.length === 0,
          },
        )}
      >
        <Tile
          titleComponent={
            <button
              aria-label={
                collapsed
                  ? t("PRODUCTS-SHOW_EFFETS_LEGEND")
                  : t("PRODUCTS-COLLAPSE_EFFETS_LEGEND")
              }
              onClick={() => {
                this.setState({ collapsed: !collapsed })
              }}
              className={"_fp-heading-4 _fp-effects-legend__title"}
            >
              <span
                className={"_fp-print-hidden _fp-effects-legend__title__icon"}
              >
                <ArrowDownIcon />
              </span>
              {t("PRODUCTS-EFFECT_LEGEND")}
            </button>
          }
          noTile={true}
          tileSize={"s"}
        >
          <div className="_fp-effects-legend__effects" style={{ height }}>
            <div ref={this.contentRef}>
              {effects.map((effect, index) => {
                return (
                  <Effect
                    key={"effect_" + index}
                    effectStyle={"full"}
                    effectBackgroundColor={effect.color}
                    effectLabel={effect.value}
                    effectTooltip={t(effect.label.key)}
                  />
                )
              })}
            </div>
          </div>
        </Tile>
      </div>
    )
  }
}

EffectsLegend.propTypes = {
  products: PropTypes.arrayOf(PropTypes.instanceOf(ProductModel)).isRequired,
  width: PropTypes.number.isRequired,
  left: PropTypes.number.isRequired,
  t: PropTypes.any,
  show: PropTypes.bool,
}

export default EffectsLegendHoc
